import { Base } from "../../framework/base";
import { createDateField, createUserNameField, ITaskTypeCol, modifyDateField, modifyUserNameField } from "./taskTypeCol";
import { TaskDataRowDataType, EnumHelper } from "../enums";
import { Translations } from "../translations";
import { TaskDataRowColIcon } from "./taskDataRowColIcon";

export interface ITaskDataRowCol {
    field: string;
    valueStr: string;
    valueNbr: number;
    valueObj: any;
    icon: TaskDataRowColIcon;

    getHash(): string;
    getTaskDataRowColListUiValue(typeCol: ITaskTypeCol, createUserName: string, createDate: number, modifyUserName: string, modifyDate: number): string;
    setTaskDataRowColValueFromDbValue(typeCol: ITaskTypeCol, value: any);
    matchesToFilter(lowFilter: string, typeCol: ITaskTypeCol, createUserName: string, createDate: number, modifyUserName: string, modifyDate: number): boolean;
}

export class TaskDataRowCol implements ITaskDataRowCol {
    field: string;
    valueStr: string;
    valueNbr: number;
    valueObj: any;
    icon: TaskDataRowColIcon;

    constructor();
    constructor(obj: ITaskDataRowCol);
    constructor(obj?: any) {
        this.field = obj && obj.field || "";
        this.valueStr = obj && obj.valueStr || "";
        this.valueNbr = obj && obj.valueNbr || null;
        this.valueObj = obj && obj.valueObj || null;
        this.icon = null;
        if (obj && obj.icon) {
            this.icon = new TaskDataRowColIcon(obj.icon);
        }
    }

    getHash(): string {
        return (this.field ?? "") + "#" +
            (this.valueStr ?? "") + "#" +
            (this.valueNbr ? this.valueNbr.toString() : "") + "#" +
            (this.valueObj ? JSON.stringify(this.valueObj) : "");
    }

    static crateTaskDataRowCol(field: string, valueStr: string, valueNbr: number): TaskDataRowCol {
        const result = new TaskDataRowCol();
        result.field = field;
        result.valueStr = valueStr;
        result.valueNbr = valueNbr;
        return result;
    }

    static getExpressionFieldTaskDataRowCols(items: ITaskDataRowCol[]): ITaskDataRowCol[] {
        if (Base.isNullOrUndefined(items)) return [];
        const result = items.slice(0);
        if (items.length > 1) {
            result.sort((a: ITaskDataRowCol, b: ITaskDataRowCol) => { return (Base.isNullOrUndefined(a) || Base.isNullOrUndefined(b) || Base.isNullOrUndefined(a.field) || Base.isNullOrUndefined(b.field)) ? 0 : (-1 * Base.numCompare(a.field.length, b.field.length)); });
        }
        return result;
    }

    static getExpressionFieldTaskTypeCols(items: ITaskTypeCol[]): ITaskTypeCol[] {
        if (!items) return [];
        const result = items.slice(0);
        if (items.length > 1) {
            result.sort((a: ITaskTypeCol, b: ITaskTypeCol) => { return (Base.isNullOrUndefined(a) || Base.isNullOrUndefined(b) || Base.isNullOrUndefined(a.field) || Base.isNullOrUndefined(b.field)) ? 0 : (-1 * Base.numCompare(a.field.length, b.field.length)); });
        }
        return result;
    }

    static getExpressionsFieldNames(typeCols: ITaskTypeCol[], expression: string): string[] {
        if (!expression || !typeCols) return [];
        const result: string[] = [];
        const cols = TaskDataRowCol.getExpressionFieldTaskTypeCols(typeCols);
        for (let i = 0; i < cols.length; i++) {
            const col = cols[i];
            const index = expression.indexOf(col.field);
            if (index < 0) continue;
            result.push(col.field);
        }
        return result;
    }

    static compareTaskDataRowCols(a: ITaskDataRowCol, b: ITaskDataRowCol, typeCol: ITaskTypeCol, asc: boolean) {
        const multiplier = asc ? 1 : -1;
        if (Base.isNullOrUndefined(a)) return multiplier * (Base.isNullOrUndefined(b) ? 0 : -1);
        if (Base.isNullOrUndefined(b)) return multiplier * 1;
        return multiplier *
            (typeCol.dataType === TaskDataRowDataType.String
                ? Base.strCompare(a.valueStr, b.valueStr)
                : Base.numCompare(a.valueNbr, b.valueNbr));
    }
    
    static strCompare(a: string, b: string, asc: boolean) {
        const multiplier = asc ? 1 : -1;
        if (Base.isNullOrUndefined(a)) return multiplier * (Base.isNullOrUndefined(b) ? 0 : -1);
        if (Base.isNullOrUndefined(b)) return multiplier * 1;
        return multiplier * Base.strCompare(a, b);
    }
    
    static numCompare(a: number, b: number, asc: boolean) {
        const multiplier = asc ? 1 : -1;
        if (Base.isNullOrUndefined(a)) return multiplier * (Base.isNullOrUndefined(b) ? 0 : -1);
        if (Base.isNullOrUndefined(b)) return multiplier * 1;
        return multiplier * Base.numCompare(a, b);
    }

    static getTimeEvalValue = (valueNbr: number): string => {
        let result = "new Date()";
        if (!Base.isNullOrUndefined(valueNbr) && !isNaN(valueNbr)) {
            const date = new Date();
            date.setTime(valueNbr);
            result = "new Date(Date.UTC(0,0,0," + date.getUTCHours() + "," + date.getUTCMinutes() + "," + date.getUTCSeconds() + "," + date.getUTCMilliseconds() + "))";
        }
        return result;
    };

    static getDateEvalValue = (valueNbr: number): string => {
        let result = "new Date()";
        if (!Base.isNullOrUndefined(valueNbr) && !isNaN(valueNbr)) {
            const date = new Date();
            date.setTime(valueNbr);
            result = "new Date(Date.UTC(" + date.getUTCFullYear() + "," + date.getUTCMonth() + "," + date.getUTCDate() + ",0,0,0,0))";
        }
        return result;
    };

    private static evaluateStrExpressionFormat(expressionFormat: string, evalValue: string, defaultValue: string = null): string {
        const defVal = !Base.isNullOrUndefined(defaultValue) ? defaultValue : evalValue;
        if (!expressionFormat) return defVal;
        const index = expressionFormat.indexOf("fieldValue");
        if (index < 0) return defVal;
        expressionFormat = expressionFormat.replace(/fieldValue/g, evalValue);
        try {
            /* eslint-disable no-eval */
            return eval(expressionFormat);
            /* eslint-enable no-eval */
        } catch (error) {
            console.log(expressionFormat, error);
            return defVal;
        }
    }

    static getDefaultValueTaskDataRowCol(typeCol: ITaskTypeCol): ITaskDataRowCol {
        const result = new TaskDataRowCol();
        result.field = typeCol.field;
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.String)) {
            const optionValues = !Base.isNullOrUndefined(typeCol.options) && typeCol.options.length > 0
                ? typeCol.options
                : (!Base.isNullOrUndefined(typeCol.idOptions) && typeCol.idOptions.length > 0
                    ? typeCol.idOptions.map(i => i.id)
                    : null);
            const isRadio = optionValues && (typeCol.class ?? "").includes("radio");
            result.valueStr = !Base.isNullOrUndefined(optionValues)
                ? typeCol.defaultStr && optionValues.indexOf(typeCol.defaultStr) > -1
                    ? typeCol.defaultStr
                    : (!typeCol.placeholder
                        ? (isRadio
                            ? ""
                            : optionValues[0])
                        : "")
                : typeCol.defaultStr ? typeCol.defaultStr : "";
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Integer) || EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Number)) {
            if (!Base.isNullOrUndefined(typeCol.defaultNbr)) {
                result.valueNbr = typeCol.defaultNbr;
            } else if (typeCol.required) {
                result.valueNbr = 0;
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Time)) {
            if (typeCol.defaultStr) {
                const hasMinus = typeCol.defaultStr[0] === "-";
                const hasPlus = typeCol.defaultStr[0] === "+";
                if (hasMinus || hasPlus) {
                    const offset = typeCol.defaultStr.substring(1).toInteger();
                    result.valueNbr = Base.getNowUtcTime().addMinutes((hasMinus ? -1 : 1) * offset).getTime();
                } else {
                    result.valueNbr = typeCol.defaultStr.toUtcTime().getTime();
                }
            } else if (typeCol.required) {
                result.valueNbr = Base.getNowUtcTime().getTime();
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Date)) {
            if (typeCol.defaultStr) {
                const hasMinus = typeCol.defaultStr[0] === "-";
                const hasPlus = typeCol.defaultStr[0] === "+";
                if (hasMinus || hasPlus) {
                    const offset = typeCol.defaultStr.substring(1).toInteger();
                    result.valueNbr = Base.getNowUtcDate().addDays((hasMinus ? -1 : 1) * offset).getTime();
                } else {
                    result.valueNbr = typeCol.defaultStr.toUtcDate().getTime();
                }
            } else if (typeCol.required) {
                result.valueNbr = Base.getNowUtcDate().getTime();
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TimeSpan)) {
            if (typeCol.defaultStr) {
                result.valueNbr = typeCol.defaultStr.toTimeSpan();
            } else if (typeCol.required) {
                result.valueNbr = 0;
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Boolean)) {
            if (typeCol.defaultStr) {
                result.valueNbr = Base.strToBool(typeCol.defaultStr) ? 1 : 0;
            } else if (typeCol.required) {
                result.valueNbr = 0;
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.StringArray) || EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TreeStringArray)) {
            const optionValues = !Base.isNullOrUndefined(typeCol.options) && typeCol.options.length > 0
                ? typeCol.options
                : (!Base.isNullOrUndefined(typeCol.idOptions) && typeCol.idOptions.length > 0
                    ? typeCol.idOptions.map(i => i.id)
                    : (!Base.isNullOrUndefined(typeCol.idNodeOptions) && typeCol.idNodeOptions.length > 0
                        ? typeCol.idNodeOptions.map(i => i.id)
                        : null));
            const defaultValues: string[] = typeCol.defaultStr
                ? JSON.parse(typeCol.defaultStr)
                : null;
            const defaultOptionsValues: string[] = optionValues && defaultValues
                ? optionValues.filter(i => defaultValues.indexOf(i) > -1)
                : [];
            result.valueStr = optionValues
                ? defaultOptionsValues.length > 0 ? JSON.stringify(defaultOptionsValues) : (!typeCol.placeholder ? JSON.stringify([optionValues[0]]) : "[]")
                : defaultValues ? JSON.stringify(defaultValues) : "[]";
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TitlePage)) {
            const nowDate = new Date();
            const y = nowDate.getFullYear();
            result.valueObj = {
                startDate: new Date(Date.UTC(y, 1, 0, 0, 0, 0, 0)).getTime(),
                endDate: new Date(Date.UTC(y, 11, 31, 0, 0, 0, 0)).getTime(),
            };
        }
        return result;
    }

    static getTestValueTaskDataRowCol(typeCol: ITaskTypeCol): ITaskDataRowCol {
        const result = new TaskDataRowCol();
        result.field = typeCol.field;
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.String)) {
            const optionValues = !Base.isNullOrUndefined(typeCol.options) && typeCol.options.length > 0
                ? typeCol.options
                : (!Base.isNullOrUndefined(typeCol.idOptions) && typeCol.idOptions.length > 0
                    ? typeCol.idOptions.map(i => i.id)
                    : null);
            result.valueStr = !Base.isNullOrUndefined(optionValues)
                ? typeCol.defaultStr && optionValues.indexOf(typeCol.defaultStr) > -1
                    ? typeCol.defaultStr
                    : (optionValues
                        ? optionValues[Base.getRandomIntInteger(optionValues.length)]
                        : ("Test" + String.fromCharCode(65 + Math.floor(Math.random() * 26))))
                : typeCol.defaultStr
                    ? typeCol.defaultStr
                    : ("Test" + String.fromCharCode(65 + Math.floor(Math.random() * 26)));
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Integer) || EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Number)) {
            if (!Base.isNullOrUndefined(typeCol.defaultNbr)) {
                result.valueNbr = typeCol.defaultNbr;
            } else {
                result.valueNbr = EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Integer)
                    ? Base.getRandomIntInteger(typeCol.maximum ? typeCol.maximum + 1 : 100, typeCol.minimum ? typeCol.minimum : 0)
                    : Base.getRandomFloat(typeCol.maximum ? typeCol.maximum + 1 : 100, typeCol.minimum ? typeCol.minimum : 0);
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Time)) {
            if (typeCol.defaultStr) {
                const hasMinus = typeCol.defaultStr[0] === "-";
                const hasPlus = typeCol.defaultStr[0] === "+";
                if (hasMinus || hasPlus) {
                    const offset = typeCol.defaultStr.substring(1).toInteger();
                    result.valueNbr = Base.getNowUtcTime().addMinutes((hasMinus ? -1 : 1) * offset).getTime();
                } else {
                    result.valueNbr = typeCol.defaultStr.toUtcTime().getTime();
                }
            } else {
                result.valueNbr = Base.getNowUtcTime().addMinutes(Base.getRandomIntInteger(60)).getTime();
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Date)) {
            if (typeCol.defaultStr) {
                const hasMinus = typeCol.defaultStr[0] === "-";
                const hasPlus = typeCol.defaultStr[0] === "+";
                if (hasMinus || hasPlus) {
                    const offset = typeCol.defaultStr.substring(1).toInteger();
                    result.valueNbr = Base.getNowUtcDate().addDays((hasMinus ? -1 : 1) * offset).getTime();
                } else {
                    result.valueNbr = typeCol.defaultStr.toUtcDate().getTime();
                }
            } else {
                result.valueNbr = Base.getNowUtcDate().addDays(Base.getRandomIntInteger(30)).getTime();
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TimeSpan)) {
            if (typeCol.defaultStr) {
                result.valueNbr = typeCol.defaultStr.toTimeSpan();
            } else {
                result.valueNbr = Base.getRandomFloat(typeCol.maximum ? typeCol.maximum + 1 : 100, typeCol.minimum ? typeCol.minimum : 0);
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Boolean)) {
            if (typeCol.defaultStr) {
                result.valueNbr = Base.strToBool(typeCol.defaultStr) ? 1 : 0;
            } else {
                result.valueNbr = Base.getRandomIntInteger(2);
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.StringArray) || EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TreeStringArray)) {
            const optionValues = !Base.isNullOrUndefined(typeCol.options) && typeCol.options.length > 0
                ? typeCol.options
                : (!Base.isNullOrUndefined(typeCol.idOptions) && typeCol.idOptions.length > 0
                    ? typeCol.idOptions.map(i => i.id)
                    : (!Base.isNullOrUndefined(typeCol.idNodeOptions) && typeCol.idNodeOptions.length > 0
                        ? typeCol.idNodeOptions.map(i => i.id)
                        : null));
            const defaultValues: string[] = typeCol.defaultStr
                ? JSON.parse(typeCol.defaultStr)
                : null;
            const defaultOptionsValues: string[] = optionValues && defaultValues
                ? optionValues.filter(i => defaultValues.indexOf(i) > -1)
                : [];
            result.valueStr = optionValues
                ? defaultOptionsValues.length > 0 ? JSON.stringify(defaultOptionsValues) : (!typeCol.placeholder ? JSON.stringify([optionValues[0]]) : "[]")
                : defaultValues ? JSON.stringify(defaultValues) : "[]";
        }
        return result;
    }

    static getTaskDataRowColUiValue(typeCol: ITaskTypeCol, valueStr: string, valueNbr: number): string {
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.String)) return TaskDataRowCol.evaluateStrExpressionFormat(typeCol.expressionFormat, valueStr ?? "");
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Integer)) {
            const isValidNumber = !Base.isNullOrUndefined(valueNbr) && !isNaN(valueNbr);
            return TaskDataRowCol.evaluateStrExpressionFormat(typeCol.expressionFormat,
                isValidNumber ? valueNbr.toString(10) : "0",
                isValidNumber ? valueNbr.toLocaleFixed(0) : (typeCol.required ? "0" : ""));
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Number)) {
            const isValidNumber = !Base.isNullOrUndefined(valueNbr) && !isNaN(valueNbr);
            return TaskDataRowCol.evaluateStrExpressionFormat(typeCol.expressionFormat,
                isValidNumber ? valueNbr.toString(10) : "0",
                isValidNumber ? valueNbr.toLocaleFixed(typeCol.decimals) : (typeCol.required ? (<number>0).toLocaleFixed(typeCol.decimals) : ""));
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Time)) {
            const isValidNumber = !Base.isNullOrUndefined(valueNbr) && !isNaN(valueNbr);
            return TaskDataRowCol.evaluateStrExpressionFormat(typeCol.expressionFormat,
                TaskDataRowCol.getTimeEvalValue(valueNbr),
                isValidNumber ? Base.utcTimeToTimeStr(valueNbr) : "");
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Date)) {
            const isValidNumber = !Base.isNullOrUndefined(valueNbr) && !isNaN(valueNbr);
            return TaskDataRowCol.evaluateStrExpressionFormat(typeCol.expressionFormat,
                TaskDataRowCol.getDateEvalValue(valueNbr),
                isValidNumber ? Base.utcTimeToDateStr(valueNbr) : "");
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TimeSpan)) {
            const isValidNumber = !Base.isNullOrUndefined(valueNbr) && !isNaN(valueNbr);
            return TaskDataRowCol.evaluateStrExpressionFormat(typeCol.expressionFormat,
                isValidNumber ? valueNbr.toString(10) : "0",
                isValidNumber ? Base.timeSpanToString(valueNbr) : "");
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Boolean)) {
            const isValidNumber = !Base.isNullOrUndefined(valueNbr) && !isNaN(valueNbr);
            return TaskDataRowCol.evaluateStrExpressionFormat(typeCol.expressionFormat,
                isValidNumber ? valueNbr.toString(10) : "0");
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.StringArray) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TreeStringArray)) return TaskDataRowCol.evaluateStrExpressionFormat(typeCol.expressionFormat, valueStr ?? "");
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Signature)) return TaskDataRowCol.evaluateStrExpressionFormat(typeCol.expressionFormat, valueStr ?? "");
        return "";
    }

    static getTaskDataRowColDbValue(typeCol: ITaskTypeCol, valueStr: string, valueNbr: number): any {
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.String) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.StringArray) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TreeStringArray) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Signature)) {
            return valueStr;
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Integer) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Boolean)) {
            return !Base.isNullOrUndefined(valueNbr) ? Math.floor(valueNbr) : null;
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Date)) {
            if (Base.isNullOrUndefined(valueNbr)) return null;
            return Base.dateToUtcDate(new Date(valueNbr)).toISOString();
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Time)) {
            if (Base.isNullOrUndefined(valueNbr)) return null;
            return new Date(valueNbr).toISOString();
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TimeSpan)) return valueNbr;
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Number)) return !Base.isNullOrUndefined(valueNbr) ? Base.roundToDecimals(valueNbr, typeCol.decimals) : null;
        return null;
    }

    static getTaskDataRowColSortValue(typeCol: ITaskTypeCol, valueStr: string, valueNbr: number): string {
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.String) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.StringArray) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TreeStringArray) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Signature)) {
            return valueStr;
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Integer) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Boolean) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Date) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Time)) {
            const value = !Base.isNullOrUndefined(valueNbr) ? Math.floor(valueNbr) : 0;
            return (value < 0 ? "-" : "+") + Base.padZeros(Math.abs(value).toString(10), 20);
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TimeSpan)) {
            const value = !Base.isNullOrUndefined(valueNbr) ? Base.roundToDecimals(valueNbr, 8) : 0;
            return (value < 0 ? "-" : "+") + Base.padZeros(Math.abs(value).toFixed(8), 20);
        }
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Number)) {
            const value = !Base.isNullOrUndefined(valueNbr) ? Base.roundToDecimals(valueNbr, typeCol.decimals) : 0;
            return (value < 0 ? "-" : "+") + Base.padZeros(Math.abs(value).toFixed(typeCol.decimals), 20);
        }
        return null;
    }

    setTaskDataRowColValueFromDbValue(typeCol: ITaskTypeCol, value: any) {
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.String) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.StringArray) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TreeStringArray) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Signature)) {
            this.valueStr = value;
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Integer) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Boolean) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TimeSpan) ||
            EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Number)) {
            this.valueNbr = value;
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Date)) {
            this.valueNbr = null;
            if (Base.isNullOrUndefined(value)) return;
            this.valueNbr = new Date(value).getTime();
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Time)) {
            this.valueNbr = null;
            if (Base.isNullOrUndefined(value)) return;
            this.valueNbr = new Date(value).getTime();
        }
    }

    getTaskDataRowColListUiValue(typeCol: ITaskTypeCol, createUserName: string, createDate: number, modifyUserName: string, modifyDate: number): string {
        let dataCol: ITaskDataRowCol = this;
        if (typeCol.field.equalIgnoreCase(createUserNameField)) {
            dataCol = TaskDataRowCol.crateTaskDataRowCol(typeCol.field, createUserName, 0);
        } else if (typeCol.field.equalIgnoreCase(createDateField)) {
            dataCol = TaskDataRowCol.crateTaskDataRowCol(typeCol.field, Base.timeToDateTimeStr(createDate), createDate);
        } else if (typeCol.field.equalIgnoreCase(modifyUserNameField)) {
            dataCol = TaskDataRowCol.crateTaskDataRowCol(typeCol.field, modifyUserName, 0);
        } else if (typeCol.field.equalIgnoreCase(modifyDateField)) {
            dataCol = TaskDataRowCol.crateTaskDataRowCol(typeCol.field, Base.timeToDateTimeStr(modifyDate), modifyDate);
        }
        let result = !Base.isNullOrUndefined(dataCol)
            ? TaskDataRowCol.getTaskDataRowColUiValue(typeCol, dataCol.valueStr, dataCol.valueNbr)
            : "";
        if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.Boolean)) {
            result = Base.strToBool(result) ? Translations.Yes : Translations.No;
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.String)) {
            if (typeCol.idOptions && typeCol.idOptions.length > 0) {
                const idOption = typeCol.idOptions.find(i => i.id === result);
                if (idOption) {
                    result = idOption.title;
                } else {
                    result = "";
                }
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.StringArray)) {
            if (typeCol.idOptions && typeCol.idOptions.length > 0 && !!result) {
                const array: string[] = JSON.parse(result);
                const idOptions = typeCol.idOptions.filter(i => array.indexOf(i.id) > -1);
                result = idOptions.map(i => i.title).join(", ");
            }
        } else if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TreeStringArray)) {
            if (typeCol.idNodeOptions && typeCol.idNodeOptions.length > 0 && !!result) {
                const array: string[] = JSON.parse(result);
                const idOptions = typeCol.idNodeOptions.filter(i => array.indexOf(i.id) > -1);
                result = idOptions.map(i => i.title).join(", ");
            }
        }
        return result;
    }

    matchesToFilter(lowFilter: string, typeCol: ITaskTypeCol, createUserName: string, createDate: number, modifyUserName: string, modifyDate: number): boolean {
        if (!typeCol) return false;
        return this.getTaskDataRowColListUiValue(typeCol, createUserName, createDate, modifyUserName, modifyDate).toLowerCase().indexOf(lowFilter) > -1;
    }

    static initializeValuesByType(typeCols: ITaskTypeCol[], dataCols: ITaskDataRowCol[]): ITaskDataRowCol[] {
        const result: ITaskDataRowCol[] = [];
        for (const typeCol of typeCols) {
            let dataCol = dataCols.find(j => j.field === typeCol.field);
            if (Base.isNullOrUndefined(dataCol)) {
                dataCol = TaskDataRowCol.getDefaultValueTaskDataRowCol(typeCol);
            }
            const newDataCol = new TaskDataRowCol(dataCol);
            newDataCol.valueStr = TaskDataRowCol.getTaskDataRowColUiValue(typeCol, dataCol.valueStr, dataCol.valueNbr);
            result.push(newDataCol);
        }
        return result;
    }

    static initializeTestValuesByType(typeCols: ITaskTypeCol[], dataCols: ITaskDataRowCol[]): ITaskDataRowCol[] {
        const result: ITaskDataRowCol[] = [];
        for (const typeCol of typeCols) {
            let dataCol = dataCols.find(j => j.field === typeCol.field);
            if (Base.isNullOrUndefined(dataCol)) {
                dataCol = TaskDataRowCol.getTestValueTaskDataRowCol(typeCol);
            }
            const newDataCol = new TaskDataRowCol(dataCol);
            newDataCol.valueStr = TaskDataRowCol.getTaskDataRowColUiValue(typeCol, dataCol.valueStr, dataCol.valueNbr);
            result.push(newDataCol);
        }
        return result;
    }
    
    static hasDefaultValues(typeCols: ITaskTypeCol[], dataCols: ITaskDataRowCol[]): boolean {
        for (const typeCol of typeCols) {
            const dataCol = dataCols.find(j => j.field === typeCol.field);
            if (Base.isNullOrUndefined(dataCol)) {
                continue;
            }
            const defaultDataCol = TaskDataRowCol.getDefaultValueTaskDataRowCol(typeCol);
            if (TaskDataRowCol.getTaskDataRowColUiValue(typeCol, dataCol.valueStr, dataCol.valueNbr) !== TaskDataRowCol.getTaskDataRowColUiValue(typeCol, defaultDataCol.valueStr, defaultDataCol.valueNbr)) {
                return false;
            }
        }
        return true;
    }

    static getToBeSavedTaskDataRowCols(formCols: ITaskTypeCol[], dataCols: ITaskDataRowCol[]): ITaskDataRowCol[] {
        const savedFormCols = formCols.filter(i => i.isSaveDataType() && i.isSavedTaskDataField());
        return dataCols.filter(i => savedFormCols.find(j => j.field === i.field));
    }
}

export class SaveTaskDataRowCol {
    field: string;
    valueStr: string;
    valueNbr: number;
    valueObj: number;

    constructor();
    constructor(obj: ITaskDataRowCol);
    constructor(obj?: any) {
        this.field = obj && obj.field || "";
        this.valueStr = obj && obj.valueStr || "";
        this.valueNbr = obj && obj.valueNbr || null;
        this.valueObj = obj && obj.valueObj || null;
    }
}